// Una fila del chat. Es el ÚNICO lugar donde se dibuja un mensaje: el panel
// lateral y la pantalla dedicada montan este mismo componente, así que no hay
// forma de que las burbujas se desincronicen entre las dos vistas.
//
// El look sale de `pages/v2/ChatPanelV2.tsx` (mismas medidas, mismos grises,
// mismo markdown compartido). Está copiado a propósito, no importado: aquel
// componente es producción de otro grupo y no se refactoriza.

import { AlertCircle, BarChart3, FileText, Image as ImageIcon, Maximize2 } from 'lucide-react';
import { renderMarkdown } from '../../utils/chatMarkdown';
import { formatearPeso } from '../adjuntosChatAtlas';
import { tieneContenidoVisible } from '../mensajesChat';
import { urlDeAdjunto } from './prepararAdjunto';
import type { AdjuntoChat, GraficoChat, MensajeChatAtlas } from '../tipos';

/** Dónde se puede dibujar el gráfico desde esta vista.
 *
 *  `'panel-derecho'` — pantalla dedicada (`/chat`): hay ancho, la tarjeta lo
 *  abre en la columna de la derecha sin moverse de la conversación.
 *  `'ir-a-pantalla'` — panel lateral: la franja mide ~420px, no entra un
 *  gráfico. La tarjeta avisa y manda a la pantalla completa. */
export type VistaGrafico = 'panel-derecho' | 'ir-a-pantalla';

/**
 * La tarjeta de una respuesta gráfica dentro de la burbuja.
 *
 * OJO: acá NO se dibuja el HTML del modelo, ni un preview, ni una miniatura.
 * Ese HTML solo se renderiza dentro del `<iframe sandbox>` de `VisorGrafico`
 * (ver `documentoGrafico.ts` para el porqué). Esta tarjeta es texto plano de
 * Atlas: título e invitación a abrirlo.
 */
const TarjetaGrafico: React.FC<{
  grafico: GraficoChat;
  vista: VistaGrafico;
  activo: boolean;
  onAbrir?: (id: string) => void;
}> = ({ grafico, vista, activo, onAbrir }) => {
  const enPantalla = vista === 'ir-a-pantalla';
  return (
    <button
      type="button"
      onClick={() => onAbrir?.(grafico.id)}
      aria-pressed={activo}
      // Radios concéntricos: contenedor 16px con 8px de padding ⇒ el ícono
      // interior va en 8px.
      className={`group flex w-full items-center gap-3 rounded-2xl border bg-white p-2 pr-3 text-left transition-colors ${
        activo
          ? 'border-[#1644EB]/[0.45] bg-[#1644EB]/[0.04]'
          : 'border-[#02062B]/[0.1] hover:border-[#1644EB]/[0.35]'
      }`}
    >
      <span
        className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
          activo ? 'bg-[#1644EB]/[0.12] text-brand-primary' : 'bg-[#F0F2F7] text-[#02062B]/[0.5]'
        }`}
      >
        <BarChart3 className="h-5 w-5" aria-hidden="true" />
      </span>

      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-medium text-brand-black">
          {grafico.titulo}
        </span>
        <span className="block truncate text-[12px] text-[#02062B]/[0.5]">
          {enPantalla
            ? 'Abrilo en pantalla completa para verlo'
            : activo
              ? 'Se está mostrando a la derecha'
              : 'Tocá para verlo a la derecha'}
        </span>
      </span>

      {enPantalla ? (
        <Maximize2
          className="h-4 w-4 shrink-0 text-[#02062B]/[0.4] transition-colors group-hover:text-brand-primary"
          aria-hidden="true"
        />
      ) : null}
    </button>
  );
};

/** Miniatura / ficha de un adjunto ya enviado, dentro de la burbuja.
 *
 *  Una imagen SIN `datos` no es un error: el caché de sesión guarda los
 *  adjuntos sin su base64 (ver `mensajesParaCache`), así que después de un
 *  refresh la miniatura no se puede dibujar y cae a la ficha con nombre y peso
 *  — que es lo mismo que se muestra para un archivo de texto. */
const AdjuntoEnviado: React.FC<{ adjunto: AdjuntoChat }> = ({ adjunto }) => {
  if (adjunto.clase === 'imagen' && adjunto.datos) {
    return (
      // `<img>` crudo y no `CachedImg` a propósito: la fuente es una `data:`
      // URL armada en memoria (ver `urlDeAdjunto`). No hay red, no hay caché
      // que compartir y no existe el parpadeo de ícono roto que motivó
      // `CachedImg` — que es para URLs remotas.
      <img
        src={urlDeAdjunto(adjunto)}
        alt={adjunto.nombre}
        className="h-[72px] w-[72px] rounded-xl border border-[#02062B]/[0.08] object-cover"
      />
    );
  }
  const Icono = adjunto.clase === 'imagen' ? ImageIcon : FileText;
  return (
    <span className="inline-flex max-w-[220px] items-center gap-2 rounded-xl border border-[#02062B]/[0.08] bg-white py-2 px-3">
      <Icono className="h-4 w-4 shrink-0 text-[#02062B]/[0.45]" aria-hidden="true" />
      <span className="min-w-0">
        <span className="block truncate text-[13px] font-medium text-brand-black">
          {adjunto.nombre}
        </span>
        <span className="block text-[11px] text-[#02062B]/[0.5]">
          {formatearPeso(adjunto.bytes)}
        </span>
      </span>
    </span>
  );
};

interface Props {
  mensaje: MensajeChatAtlas;
  /** Se llama al tocar un chip de sugerencia. */
  onSugerencia?: (texto: string) => void;
  /** Con el turno en curso las sugerencias no se pueden tocar. */
  bloqueado?: boolean;
  /** Qué hace la tarjeta de un gráfico en esta vista. */
  vistaGrafico?: VistaGrafico;
  /** Se llama al tocar la tarjeta de un gráfico, con su id. */
  onAbrirGrafico?: (id: string) => void;
  /** Gráfico que se está mirando, para marcarlo en la lista. */
  graficoActivoId?: string | null;
}

export const BurbujaMensaje: React.FC<Props> = ({
  mensaje,
  onSugerencia,
  bloqueado,
  vistaGrafico = 'ir-a-pantalla',
  onAbrirGrafico,
  graficoActivoId,
}) => {
  if (mensaje.rol === 'user') {
    return (
      <div className="mt-6 flex flex-col items-end gap-2 first:mt-0">
        {mensaje.adjuntos && mensaje.adjuntos.length > 0 && (
          <div className="flex max-w-[80%] flex-wrap justify-end gap-2">
            {mensaje.adjuntos.map((a) => (
              <AdjuntoEnviado key={a.id} adjunto={a} />
            ))}
          </div>
        )}
        {mensaje.texto && (
          <div className="max-w-[80%] whitespace-pre-wrap rounded-[18px] rounded-tr-[6px] bg-[#F2F2F7] py-2.5 px-4 text-[15px] leading-snug text-[#02062B]/[0.9]">
            {mensaje.texto}
          </div>
        )}
      </div>
    );
  }

  // Aviso de error del cliente: se distingue del texto del modelo, porque no
  // salió del modelo. Ámbar y no rojo: casi siempre es "probá de nuevo", no
  // una falla del usuario.
  if (mensaje.error) {
    return (
      <div className="flex items-start gap-2 rounded-xl bg-[#F7EBD5] py-2.5 px-3.5 text-[13.5px] font-medium text-[#8A5A08]">
        <AlertCircle className="mt-px h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{mensaje.texto}</span>
      </div>
    );
  }

  // Sin texto, sin gráficos y sin sugerencias no se dibuja NADA: un
  // `<div class="flex flex-col gap-3">` vacío dentro de una lista con `gap-5`
  // es un hueco de ~20px que el usuario lee como un error. El motor ya saca
  // esas burbujas del hilo; esto es el segundo cerrojo, en el render.
  if (!tieneContenidoVisible(mensaje)) return null;

  return (
    <div className="flex flex-col gap-3">
      {mensaje.texto && (
        <div className="text-[15px] leading-relaxed text-[#02062B]/[0.85] [&_a]:text-brand-primary">
          {renderMarkdown(mensaje.texto)}
        </div>
      )}

      {/* Respuestas gráficas. El HTML del modelo NO se toca acá: la tarjeta es
          un botón de Atlas y el dibujo pasa siempre por `VisorGrafico`
          (`<iframe sandbox="allow-scripts">` con CSP propia). */}
      {(mensaje.graficos ?? []).map((g) => (
        <TarjetaGrafico
          key={g.id}
          grafico={g}
          vista={vistaGrafico}
          activo={vistaGrafico === 'panel-derecho' && graficoActivoId === g.id}
          onAbrir={onAbrirGrafico}
        />
      ))}

      {/* Respuesta cortada a mitad de camino (el stream cerró sin
          `stop_reason`). El texto es del modelo y se muestra igual, pero sin
          este aviso no hay forma de distinguirlo de una respuesta terminada. */}
      {mensaje.truncado && (
        <p className="flex items-center gap-1.5 text-[12.5px] font-medium text-[#8A5A08]">
          <AlertCircle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          Se cortó acá: puede faltar el final.
        </p>
      )}

      {mensaje.sugerencias && mensaje.sugerencias.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {mensaje.sugerencias.map((s, i) => (
            <button
              key={`${i}-${s}`}
              type="button"
              disabled={bloqueado}
              onClick={() => onSugerencia?.(s)}
              className="rounded-full border border-[#02062B]/[0.1] bg-white py-2 px-4 text-[13px] font-medium text-brand-black transition-colors hover:border-[#1644EB]/[0.35] hover:text-brand-primary disabled:opacity-50"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default BurbujaMensaje;
